import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  CheckCircle2, 
  Bot,
  Code,
  Cog,
  ShoppingCart,
  Megaphone,
  Users,
  Smartphone,
  Globe,
  Database,
  Layers,
  BarChart3,
  Workflow,
  Search,
  Share2,
  Target,
  Palette,
  TrendingUp,
  Zap,
  Shield,
  Clock,
  DollarSign,
  FileText,
  Settings,
  Lightbulb,
  Award,
  Sparkles,
  ChevronRight,
  Play
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  FadeInUp,
  StaggerContainer,
  StaggerItem,
  AnimatedBlobs,
  AnimatedCounter
} from '../components/AnimationComponents';

// Main Services Data for Overview Grid
const mainServicesData = [
  {
    id: 'ai-solutions',
    title: 'AI Solutions',
    subtitle: 'Intelligent Automation',
    description: 'Enterprise-grade AI at SMB-friendly prices. Chatbots, predictive analytics, and automation that actually works.',
    icon: Bot,
    color: 'from-blue-600 to-cyan-500',
    bgColor: 'bg-blue-50',
    iconColor: 'text-blue-600',
    href: '/ai-solutions',
    stats: '340% avg ROI'
  },
  {
    id: 'product-engineering',
    title: 'Product Engineering',
    subtitle: 'Build What Matters',
    description: 'Web apps, mobile apps, and custom software built to scale. From MVP to enterprise-grade SaaS.',
    icon: Code,
    color: 'from-purple-600 to-pink-500',
    bgColor: 'bg-purple-50',
    iconColor: 'text-purple-600',
    href: '/services/product-engineering',
    stats: '50+ products launched'
  },
  {
    id: 'business-automation',
    title: 'Business Automation',
    subtitle: 'Work Smarter',
    description: 'Eliminate manual work. Connect your systems. Get real-time visibility into operations.',
    icon: Cog,
    color: 'from-emerald-600 to-teal-500',
    bgColor: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    href: '/services/business-automation',
    stats: '60% time saved'
  },
  {
    id: 'erp-ecommerce',
    title: 'ERP & Ecommerce',
    subtitle: 'Unified Commerce',
    description: 'Modern ERP systems and ecommerce platforms that grow with your business.',
    icon: ShoppingCart,
    color: 'from-orange-600 to-amber-500',
    bgColor: 'bg-orange-50',
    iconColor: 'text-orange-600',
    href: '/services/erp-ecommerce',
    stats: '85% efficiency gain'
  },
  {
    id: 'digital-marketing',
    title: 'Digital Marketing',
    subtitle: 'Get Found & Convert',
    description: 'AI-powered SEO, GEO optimization, social media, and paid ads that deliver measurable ROI.',
    icon: Megaphone,
    color: 'from-rose-600 to-pink-500',
    bgColor: 'bg-rose-50',
    iconColor: 'text-rose-600',
    href: '/services/digital-marketing',
    stats: '450% traffic increase'
  },
  {
    id: 'consulting',
    title: 'Consulting & Advisory',
    subtitle: 'Expert Guidance',
    description: 'Strategic consulting for digital transformation, AI adoption, and business scalability.',
    icon: Users,
    color: 'from-indigo-600 to-violet-500',
    bgColor: 'bg-indigo-50',
    iconColor: 'text-indigo-600',
    href: '/services/consulting',
    stats: '200+ projects delivered'
  }
];

// Detailed Service Pages Data
const serviceDetailsData = {
  'product-engineering': {
    id: 'product-engineering',
    title: 'Product Engineering',
    metaTitle: 'Product Engineering Services Canada | Web & Mobile App Development',
    metaDescription: 'Custom software development, web apps, mobile apps, and SaaS solutions for Canadian businesses. From MVP to enterprise scale.',
    heroHeadline: "Build Products That Users Actually Want",
    heroSubheadline: "Stop guessing. Start shipping. We turn your ideas into scalable products, from first prototype to market leader.",
    icon: Code,
    color: 'from-purple-600 to-pink-500',
    bgColor: 'bg-purple-50',
    iconColor: 'text-purple-600',
    lightColor: 'bg-purple-100',
    
    stats: [
      { value: 50, suffix: '+', label: 'Products Launched' },
      { value: 4.9, suffix: '/5', label: 'Client Rating' },
      { value: 95, suffix: '%', label: 'On-Time Delivery' }
    ],
    
    overview: "Whether you're a startup with a napkin sketch or an enterprise modernizing legacy systems, we build software that scales. Our Canadian engineering team combines technical excellence with business acumen, because great code means nothing if it doesn't solve real problems.",
    
    subServices: [
      {
        icon: Globe,
        title: 'Web Application Development',
        description: 'Custom web apps built with modern frameworks. React, Next.js, Vue, whatever fits your needs.',
        features: ['Progressive Web Apps (PWA)', 'Single Page Applications', 'Enterprise Portals', 'Customer Dashboards']
      },
      {
        icon: Smartphone,
        title: 'Mobile App Development',
        description: 'Native and cross-platform mobile apps for iOS and Android. React Native, Flutter, or native Swift/Kotlin.',
        features: ['iOS & Android Apps', 'Cross-Platform Development', 'App Store Optimization', 'Push Notifications & Analytics']
      },
      {
        icon: Layers,
        title: 'Custom Software Development',
        description: 'Bespoke software solutions tailored to your unique business processes and requirements.',
        features: ['Legacy System Modernization', 'API Development', 'Third-Party Integrations', 'Database Design']
      },
      {
        icon: Database,
        title: 'SaaS Development',
        description: 'Multi-tenant SaaS platforms built for scale. Subscription billing, user management, and analytics included.',
        features: ['Multi-Tenant Architecture', 'Subscription Management', 'Usage Analytics', 'White-Label Solutions']
      }
    ],
    
    benefits: [
      { title: 'Faster Time to Market', description: 'Ship your MVP in weeks, not months. Agile development with weekly deliverables.' },
      { title: 'Scalable Architecture', description: 'Built to handle 10 users or 10 million. Cloud-native from day one.' },
      { title: 'Ongoing Support', description: 'We don\'t disappear after launch. Maintenance, updates, and 24/7 support available.' },
      { title: 'Canadian Team', description: 'Work with developers in your timezone. No offshore handoffs or communication delays.' },
      { title: 'Fixed-Price Options', description: 'Know your costs upfront. No surprise invoices or scope creep.' },
      { title: 'Full Ownership', description: 'You own 100% of the code. No licensing fees or vendor lock-in.' }
    ],
    
    process: [
      { step: '01', title: 'Discovery', description: 'We dig into your business goals, user needs, and technical requirements. No code yet, just clarity.' },
      { step: '02', title: 'Design', description: 'Wireframes, prototypes, and architecture decisions. You see exactly what we\'re building before we build it.' },
      { step: '03', title: 'Development', description: 'Agile sprints with weekly demos. You\'re involved every step, no 6-month surprises.' },
      { step: '04', title: 'Launch & Scale', description: 'We handle deployment, monitoring, and optimization. Then we help you scale.' }
    ],
    
    faqs: [
      { question: 'How long does it take to build an MVP?', answer: 'Most MVPs take 8-12 weeks from kickoff to launch. Complex products may take longer. We\'ll give you a realistic timeline during discovery.' },
      { question: 'Do you work with startups?', answer: 'Absolutely. About 40% of our clients are startups. We offer flexible engagement models including equity partnerships for the right projects.' },
      { question: 'What technologies do you use?', answer: 'We\'re technology-agnostic and choose the best tools for your project. Common stacks include React/Node, Python/Django, React Native, and Flutter.' },
      { question: 'Can you take over an existing project?', answer: 'Yes. We regularly rescue troubled projects. We\'ll audit your codebase, identify issues, and create a remediation plan.' }
    ],
    
    ctaText: 'Start Your Product Journey'
  },
  
  'business-automation': {
    id: 'business-automation',
    title: 'Business Automation',
    metaTitle: 'Business Automation Solutions Canada | Workflow & Process Automation',
    metaDescription: 'Automate repetitive tasks, connect your systems, and get real-time business insights. Custom automation for Canadian SMBs.',
    heroHeadline: "Stop Doing Work That Machines Should Do",
    heroSubheadline: "Your team is too valuable for copy-paste tasks. We automate the mundane so humans can focus on what matters.",
    icon: Cog,
    color: 'from-emerald-600 to-teal-500',
    bgColor: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    lightColor: 'bg-emerald-100',
    
    stats: [
      { value: 60, suffix: '%', label: 'Time Saved on Tasks' },
      { value: 15, suffix: '+', label: 'Hours Saved Weekly' },
      { value: 99, suffix: '%', label: 'Process Accuracy' }
    ],
    
    overview: "Every hour your team spends on data entry, report generation, or system updates is an hour not spent on growth. We identify automation opportunities, build custom workflows, and connect your existing tools, so your business runs itself.",
    
    subServices: [
      {
        icon: Workflow,
        title: 'Workflow Automation',
        description: 'Automate multi-step processes across departments. From approval workflows to customer onboarding.',
        features: ['Custom Workflow Design', 'Conditional Logic & Branching', 'Notifications & Escalations', 'Audit Trails']
      },
      {
        icon: Database,
        title: 'Software Integration & Migration',
        description: 'Connect your existing tools. Migrate from legacy systems. No more manual data transfer.',
        features: ['API Integrations', 'Legacy System Migration', 'Data Synchronization', 'Custom Connectors']
      },
      {
        icon: BarChart3,
        title: 'Unified Dashboard & Business Insights',
        description: 'See your entire business in one view. Real-time KPIs, automated reports, and predictive analytics.',
        features: ['Custom Dashboards', 'Automated Reporting', 'KPI Tracking', 'Predictive Analytics']
      },
      {
        icon: FileText,
        title: 'Document Automation',
        description: 'Generate invoices, contracts, and reports automatically. Extract data from documents with AI.',
        features: ['Template-Based Generation', 'AI Data Extraction', 'E-Signature Integration', 'Version Control']
      }
    ],
    
    benefits: [
      { title: 'Eliminate Manual Work', description: 'Stop copying data between systems. Automation handles the repetitive stuff.' },
      { title: 'Reduce Errors', description: 'Machines don\'t fat-finger numbers. Get 99%+ accuracy on routine tasks.' },
      { title: 'Scale Without Hiring', description: 'Handle 10x the volume without 10x the headcount.' },
      { title: 'Real-Time Visibility', description: 'Know what\'s happening in your business right now, not last month.' },
      { title: 'Happy Employees', description: 'Your team wants meaningful work. Give it to them.' },
      { title: 'Fast ROI', description: 'Most automation projects pay for themselves in 3-6 months.' }
    ],
    
    process: [
      { step: '01', title: 'Process Audit', description: 'We map your current workflows and identify the highest-value automation opportunities.' },
      { step: '02', title: 'Solution Design', description: 'Custom automation architecture that fits your systems and scales with your growth.' },
      { step: '03', title: 'Implementation', description: 'We build, test, and deploy, with minimal disruption to daily operations.' },
      { step: '04', title: 'Optimization', description: 'Continuous monitoring and refinement to maximize efficiency gains.' }
    ],
    
    faqs: [
      { question: 'What tools do you integrate with?', answer: 'We work with virtually any system, CRMs (Salesforce, HubSpot), ERPs (SAP, NetSuite), accounting (QuickBooks, Xero), and hundreds more. If it has an API, we can connect it.' },
      { question: 'Will automation replace our employees?', answer: 'No, it frees them. Automation handles repetitive tasks so your team can focus on work that requires human judgment, creativity, and relationship-building.' },
      { question: 'How do you handle sensitive data?', answer: 'All data is encrypted in transit and at rest. We follow SOC 2 practices and are fully PIPEDA compliant. Your data stays in Canada.' },
      { question: 'What\'s the typical ROI timeline?', answer: 'Most clients see positive ROI within 3-6 months. We\'ll project your specific savings during the discovery phase.' }
    ],
    
    ctaText: 'Automate Your Business'
  },
  
  'erp-ecommerce': {
    id: 'erp-ecommerce',
    title: 'ERP & Ecommerce',
    metaTitle: 'ERP Solutions & Ecommerce Development Canada | Odoo, Shopify, Custom',
    metaDescription: 'Modern ERP implementation and ecommerce solutions for Canadian businesses. Unified operations, inventory, and sales.',
    heroHeadline: "One System to Run Your Entire Business",
    heroSubheadline: "Disconnected tools are killing your efficiency. We unify your operations, inventory, sales, and finance into a single source of truth.",
    icon: ShoppingCart,
    color: 'from-orange-600 to-amber-500',
    bgColor: 'bg-orange-50',
    iconColor: 'text-orange-600',
    lightColor: 'bg-orange-100',
    
    stats: [
      { value: 85, suffix: '%', label: 'Efficiency Improvement' },
      { value: 40, suffix: '%', label: 'Inventory Cost Reduction' },
      { value: 3, suffix: 'x', label: 'Faster Order Processing' }
    ],
    
    overview: "Your business has outgrown spreadsheets and disconnected software. We implement modern ERP systems and ecommerce platforms that give you complete visibility and control, from first click to final delivery.",
    
    subServices: [
      {
        icon: Database,
        title: 'ERP Implementation',
        description: 'Full ERP deployment with Odoo, ERPNext, or custom solutions. Finance, inventory, HR, and operations unified.',
        features: ['Odoo Implementation', 'ERPNext Setup', 'Custom ERP Development', 'Data Migration']
      },
      {
        icon: ShoppingCart,
        title: 'Ecommerce Development',
        description: 'Shopify, WooCommerce, or custom storefronts. Built to convert visitors into customers.',
        features: ['Shopify Development', 'WooCommerce Solutions', 'Headless Commerce', 'B2B Ecommerce']
      },
      {
        icon: Layers,
        title: 'Omnichannel Integration',
        description: 'Connect online and offline sales. Unified inventory, orders, and customer data across all channels.',
        features: ['POS Integration', 'Marketplace Connections', 'Inventory Sync', 'Unified Customer View']
      },
      {
        icon: BarChart3,
        title: 'Business Intelligence',
        description: 'Dashboards and reports that tell you what\'s actually happening in your business.',
        features: ['Sales Analytics', 'Inventory Forecasting', 'Customer Insights', 'Financial Reporting']
      }
    ],
    
    benefits: [
      { title: 'Single Source of Truth', description: 'No more conflicting spreadsheets. One system for all your data.' },
      { title: 'Automated Operations', description: 'Orders flow from website to warehouse automatically.' },
      { title: 'Real-Time Inventory', description: 'Know what you have, where it is, and when to reorder.' },
      { title: 'Better Decisions', description: 'Dashboards show exactly what\'s working and what isn\'t.' },
      { title: 'Scale-Ready', description: 'Add new channels, locations, and products without breaking.' },
      { title: 'Canadian Support', description: 'Local team that understands Canadian tax, shipping, and compliance.' }
    ],
    
    process: [
      { step: '01', title: 'Business Analysis', description: 'We map your processes, pain points, and requirements. No generic implementations.' },
      { step: '02', title: 'System Design', description: 'Custom configuration that matches how your business actually works.' },
      { step: '03', title: 'Data Migration', description: 'Safe, complete transfer of your existing data. Nothing gets lost.' },
      { step: '04', title: 'Training & Go-Live', description: 'Your team is confident before we flip the switch. Then ongoing support.' }
    ],
    
    faqs: [
      { question: 'Odoo vs ERPNext vs Custom, which should I choose?', answer: 'It depends on your needs. Odoo is great for growing SMBs. ERPNext suits companies wanting open-source flexibility. Custom makes sense for unique requirements. We\'ll recommend the right fit.' },
      { question: 'How long does ERP implementation take?', answer: 'Basic implementations take 8-12 weeks. Complex, multi-location deployments can take 4-6 months. We phase the rollout to minimize disruption.' },
      { question: 'Can you migrate from our current system?', answer: 'Yes. We\'ve migrated data from QuickBooks, Sage, legacy ERPs, and custom databases. We validate every record to ensure nothing is lost.' },
      { question: 'Do you support Shopify Plus?', answer: 'Yes. We\'re Shopify Partners and work with standard Shopify, Shopify Plus, and headless implementations with Hydrogen.' }
    ],
    
    ctaText: 'Unify Your Operations'
  },
  
  'digital-marketing': {
    id: 'digital-marketing',
    title: 'Digital Marketing',
    metaTitle: 'Digital Marketing Agency Canada | AI SEO, GEO, PPC & Social Media',
    metaDescription: 'Data-driven digital marketing for Canadian businesses. AI-powered SEO, GEO optimization, social media management, and paid advertising.',
    heroHeadline: "Get Found. Get Chosen. Get Results.",
    heroSubheadline: "Your competitors are already investing in digital. We help you outrank them, outconvert them, and outgrow them, with measurable ROI.",
    icon: Megaphone,
    color: 'from-rose-600 to-pink-500',
    bgColor: 'bg-rose-50',
    iconColor: 'text-rose-600',
    lightColor: 'bg-rose-100',
    
    stats: [
      { value: 450, suffix: '%', label: 'Avg Traffic Increase' },
      { value: 35, suffix: '%', label: 'Lower Customer Acquisition Cost' },
      { value: 4.2, suffix: 'x', label: 'Return on Ad Spend' }
    ],
    
    overview: "Digital marketing isn't about vanity metrics. It's about revenue. We combine AI-powered optimization with human creativity to drive qualified traffic that actually converts, and we prove it with transparent reporting.",
    
    subServices: [
      {
        icon: Search,
        title: 'SEO & Content Marketing',
        description: 'Rank higher, get more traffic, convert more visitors. AI-powered SEO that adapts to algorithm changes.',
        features: ['AI SEO Optimization', 'GEO (Generative Engine Optimization)', 'Technical SEO Audits', 'Ecommerce SEO', 'Content Strategy & Creation', 'Link Building']
      },
      {
        icon: Share2,
        title: 'Social Media Management',
        description: 'Build your brand where your customers spend time. Strategy, content, and community management.',
        features: ['Content Calendar & Strategy', 'Community Management', 'Influencer Partnerships', 'Social Listening']
      },
      {
        icon: Target,
        title: 'PPC & Paid Advertising',
        description: 'Google Ads, Meta, LinkedIn, campaigns that maximize ROAS with AI-driven optimization.',
        features: ['Google Ads Management', 'Meta (Facebook/Instagram) Ads', 'LinkedIn Advertising', 'Retargeting Campaigns']
      },
      {
        icon: Palette,
        title: 'Brand Strategy & Analytics',
        description: 'Build a brand that stands out. Track everything with dashboards that show real impact.',
        features: ['Brand Positioning', 'Visual Identity', 'Messaging Framework', 'Performance Analytics']
      }
    ],
    
    seoDetails: {
      title: 'SEO Services Breakdown',
      services: [
        { name: 'AI SEO', description: 'Machine learning optimization that adapts to search algorithm changes in real-time.' },
        { name: 'GEO (Generative Engine Optimization)', description: 'Optimize for AI search engines like ChatGPT, Perplexity, and Google AI Mode.' },
        { name: 'Technical SEO', description: 'Site speed, Core Web Vitals, schema markup, and crawlability improvements.' },
        { name: 'Ecommerce SEO', description: 'Product page optimization, category structure, and shopping feed management.' },
        { name: 'Local SEO', description: 'Google Business Profile, local citations, and location-based optimization for Canadian markets.' },
        { name: 'SEO Copywriting', description: 'Content that ranks and converts, written for humans, optimized for search.' }
      ]
    },
    
    benefits: [
      { title: 'Measurable ROI', description: 'Every dollar tracked. Every campaign tied to revenue.' },
      { title: 'AI-Powered Optimization', description: 'Algorithms that work 24/7 to improve your results.' },
      { title: 'Full-Funnel Strategy', description: 'From awareness to conversion to retention.' },
      { title: 'Transparent Reporting', description: 'Dashboards you can actually understand. No vanity metrics.' },
      { title: 'Canadian Market Expertise', description: 'We know the Canadian digital landscape and consumer behavior.' },
      { title: 'Agile Execution', description: 'Quick pivots based on data, not quarterly planning cycles.' }
    ],
    
    process: [
      { step: '01', title: 'Audit & Strategy', description: 'Deep dive into your current performance, competitors, and opportunities.' },
      { step: '02', title: 'Campaign Setup', description: 'Build campaigns, create content, configure tracking. Nothing launches without approval.' },
      { step: '03', title: 'Execute & Optimize', description: 'Daily monitoring and weekly optimization. AI + human oversight.' },
      { step: '04', title: 'Report & Scale', description: 'Monthly reporting with clear next steps. Double down on what works.' }
    ],
    
    faqs: [
      { question: 'What\'s GEO and why does it matter?', answer: 'GEO (Generative Engine Optimization) optimizes your content for AI search engines like ChatGPT, Perplexity, and Google\'s AI features. As more people use AI for search, GEO ensures you\'re found in those answers.' },
      { question: 'How long until I see SEO results?', answer: 'Typically 3-6 months for significant organic traffic improvements. We focus on quick wins early while building long-term authority.' },
      { question: 'Do you require long-term contracts?', answer: 'We offer month-to-month engagements after an initial 3-month commitment. We earn your business every month, no lock-in.' },
      { question: 'How do you report on results?', answer: 'Live dashboards with daily updates, plus monthly strategy calls. You always know exactly what\'s happening and why.' }
    ],
    
    ctaText: 'Grow Your Digital Presence'
  },
  
  'consulting': {
    id: 'consulting',
    title: 'Consulting & Advisory',
    metaTitle: 'Technology Consulting Canada | AI Strategy, Digital Transformation',
    metaDescription: 'Expert consulting for digital transformation, AI adoption, and business scalability. Strategic advisory for Canadian SMBs.',
    heroHeadline: "Stop Guessing. Start Knowing.",
    heroSubheadline: "Technology decisions are expensive to get wrong. We bring clarity to complexity, so you invest in the right solutions from day one.",
    icon: Users,
    color: 'from-indigo-600 to-violet-500',
    bgColor: 'bg-indigo-50',
    iconColor: 'text-indigo-600',
    lightColor: 'bg-indigo-100',
    
    stats: [
      { value: 200, suffix: '+', label: 'Projects Delivered' },
      { value: 50, suffix: 'M+', prefix: '$', label: 'Client Investment Guided' },
      { value: 98, suffix: '%', label: 'Client Satisfaction' }
    ],
    
    overview: "You don't need another 100-page report that sits on a shelf. You need actionable advice from people who've built and scaled technology businesses. We cut through vendor hype and give you honest, practical guidance.",
    
    subServices: [
      {
        icon: Target,
        title: 'Project Assessment & Advisory',
        description: 'Before you invest, know exactly what you\'re getting into. We evaluate vendors, architectures, and roadmaps.',
        features: ['Vendor Evaluation', 'Architecture Review', 'Risk Assessment', 'Roadmap Planning']
      },
      {
        icon: Bot,
        title: 'AI Consulting & Scalability',
        description: 'Navigate the AI hype. We identify real opportunities, build proof-of-concepts, and scale what works.',
        features: ['AI Readiness Assessment', 'Use Case Identification', 'Proof of Concept Development', 'AI Governance']
      },
      {
        icon: Users,
        title: 'Workforce Solutions',
        description: 'Technology is only part of the equation. We help you build teams, manage change, and upskill employees.',
        features: ['Team Augmentation', 'Change Management', 'Training Programs', 'Hiring Strategy']
      },
      {
        icon: TrendingUp,
        title: 'Digital Transformation Strategy',
        description: 'Comprehensive roadmaps for modernizing your business. Technology, process, and people aligned.',
        features: ['Current State Analysis', 'Future State Design', 'Prioritized Roadmap', 'Business Case Development']
      }
    ],
    
    benefits: [
      { title: 'Vendor-Agnostic Advice', description: 'We recommend what\'s right for you, not what pays us the best commission.' },
      { title: 'Practical, Not Theoretical', description: 'Our consultants have built products and run teams. We know what actually works.' },
      { title: 'Quick Time to Value', description: 'Engagements measured in weeks, not months. Answers, not endless analysis.' },
      { title: 'Implementation Support', description: 'We don\'t just advise, we can stay to help you execute.' },
      { title: 'Knowledge Transfer', description: 'Your team learns with us. No dependency on consultants.' },
      { title: 'Canadian Business Context', description: 'We understand the Canadian market, regulations, and business environment.' }
    ],
    
    process: [
      { step: '01', title: 'Scoping', description: 'We define the question you\'re trying to answer. Clear scope, clear deliverables.' },
      { step: '02', title: 'Discovery', description: 'Deep dive into your business, technology, and goals. Interviews, data analysis, market research.' },
      { step: '03', title: 'Analysis', description: 'We synthesize findings into actionable recommendations with clear tradeoffs.' },
      { step: '04', title: 'Delivery', description: 'Presentation of findings, roadmap, and, if desired, support for implementation.' }
    ],
    
    faqs: [
      { question: 'How is this different from big consulting firms?', answer: 'We\'re smaller, faster, and more practical. You work directly with senior consultants, not junior analysts following playbooks. And we can actually implement what we recommend.' },
      { question: 'What\'s the typical engagement length?', answer: 'Most advisory engagements are 4-8 weeks. We scope tightly and deliver quickly. Extended engagements are available for implementation support.' },
      { question: 'Do you help with AI strategy?', answer: 'Yes. AI consulting is one of our core offerings. We help you cut through the hype, identify real opportunities, and build proof-of-concepts before major investment.' },
      { question: 'Can you help hire technical talent?', answer: 'Yes. We advise on hiring strategy, help write job descriptions, evaluate candidates, and can provide interim leadership while you build your team.' }
    ],
    
    ctaText: 'Get Expert Guidance'
  }
};

// Services Overview Page Component
export const ServicesOverviewPage = () => {
  return (
    <div data-testid="services-overview-page">
      {/* Hero */}
      <section className="section-padding bg-[#0A2540] relative overflow-hidden">
        <AnimatedBlobs />
        <div className="container-custom text-center relative z-10">
          <FadeInUp>
            <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">Our Services</span>
          </FadeInUp>
          <FadeInUp delay={0.1}>
            <h1 className="font-outfit text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
              Comprehensive Digital Solutions
            </h1>
          </FadeInUp>
          <FadeInUp delay={0.2}>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              From AI automation to product engineering, we help Canadian businesses build, grow, and compete in the digital economy.
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" staggerDelay={0.1}>
            {mainServicesData.map((service, index) => (
              <StaggerItem key={service.id}>
                <Link to={service.href}>
                  <motion.div
                    className="group h-full bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:border-slate-200 transition-all"
                    whileHover={{ y: -8 }}
                    data-testid={`service-card-${service.id}`}
                  >
                    <motion.div 
                      className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center mb-6`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <service.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    
                    <h3 className="font-outfit text-xl font-semibold text-[#0A2540] mb-2 group-hover:text-[#2563EB] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-[#2563EB] text-sm font-medium mb-3">{service.subtitle}</p>
                    <p className="text-slate-600 mb-6">{service.description}</p>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <span className={`text-sm font-medium ${service.iconColor}`}>{service.stats}</span>
                      <motion.span 
                        className="text-[#2563EB] flex items-center gap-1"
                        whileHover={{ x: 5 }}
                      >
                        Learn more <ArrowRight className="w-4 h-4" />
                      </motion.span>
                    </div>
                  </motion.div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <FadeInUp className="text-center mb-12">
            <h2 className="font-outfit text-3xl font-bold text-[#0A2540]">
              Why Canadian Businesses Choose Us
            </h2>
          </FadeInUp>
          
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" staggerDelay={0.1}>
            {[
              { icon: Shield, title: 'Canadian-Based', description: 'Local team, Canadian servers, PIPEDA compliant.' },
              { icon: Zap, title: 'Fast Delivery', description: 'Agile approach with weekly deliverables.' },
              { icon: DollarSign, title: 'Transparent Pricing', description: 'No surprise invoices. Know costs upfront.' },
              { icon: Award, title: 'Proven Results', description: '200+ successful projects delivered.' }
            ].map((item, index) => (
              <StaggerItem key={index}>
                <motion.div 
                  className="bg-white p-6 rounded-xl border border-slate-100 text-center"
                  whileHover={{ y: -5 }}
                >
                  <div className="w-12 h-12 bg-[#2563EB]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-6 h-6 text-[#2563EB]" />
                  </div>
                  <h3 className="font-semibold text-[#0A2540] mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.description}</p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-br from-[#2563EB] to-[#1D4ED8]">
        <div className="container-custom text-center">
          <FadeInUp>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-6">
              Not Sure Where to Start?
            </h2>
          </FadeInUp>
          <FadeInUp delay={0.2}>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Book a free consultation and we'll recommend the right solutions for your business.
            </p>
          </FadeInUp>
          <FadeInUp delay={0.4}>
            <Link to="/contact">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                <Button className="bg-white text-[#2563EB] hover:bg-slate-100 rounded-full px-8 py-6 text-lg shadow-xl">
                  Book a Discovery Call
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </Link>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
};

// Service Detail Page Component
export const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const service = serviceDetailsData[serviceId];

  if (!service) {
    return (
      <div className="section-padding text-center" data-testid="service-not-found">
        <h1 className="font-outfit text-3xl font-bold text-[#0A2540] mb-4">Service Not Found</h1>
        <Link to="/services">
          <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full">
            View All Services
          </Button>
        </Link>
      </div>
    );
  }

  const IconComponent = service.icon;

  return (
    <div data-testid={`service-page-${serviceId}`}>
      {/* Hero Section */}
      <section className="section-padding bg-[#0A2540] relative overflow-hidden">
        <AnimatedBlobs />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <FadeInUp>
              <div className={`inline-flex items-center gap-2 ${service.lightColor} px-4 py-2 rounded-full mb-6`}>
                <IconComponent className={`w-5 h-5 ${service.iconColor}`} />
                <span className={`text-sm font-medium ${service.iconColor}`}>{service.title}</span>
              </div>
            </FadeInUp>
            
            <FadeInUp delay={0.1}>
              <h1 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-6">
                {service.heroHeadline}
              </h1>
            </FadeInUp>
            
            <FadeInUp delay={0.2}>
              <p className="text-white/70 text-lg mb-8">
                {service.heroSubheadline}
              </p>
            </FadeInUp>
            
            <FadeInUp delay={0.3}>
              <Link to="/contact">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg">
                    {service.ctaText}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>
              </Link>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 bg-white border-b border-slate-100">
        <div className="container-custom">
          <StaggerContainer className="grid grid-cols-3 gap-8" staggerDelay={0.1}>
            {service.stats.map((stat, index) => (
              <StaggerItem key={index}>
                <div className="text-center">
                  <div className={`text-3xl md:text-4xl font-bold bg-gradient-to-r ${service.color} bg-clip-text text-transparent`}>
                    {stat.prefix}<AnimatedCounter end={stat.value} suffix={stat.suffix} duration={2} />
                  </div>
                  <div className="text-sm text-slate-600 mt-1">{stat.label}</div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Overview Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <FadeInUp>
              <p className="text-lg text-slate-700 leading-relaxed">
                {service.overview}
              </p>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* Sub-Services Grid */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <FadeInUp className="text-center mb-12">
            <h2 className="font-outfit text-3xl font-bold text-[#0A2540]">
              What We Offer
            </h2>
          </FadeInUp>
          
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-8" staggerDelay={0.1}>
            {service.subServices.map((subService, index) => (
              <StaggerItem key={index}>
                <motion.div 
                  className="bg-white p-8 rounded-2xl border border-slate-100 h-full group hover:shadow-xl hover:border-slate-200 transition-all"
                  whileHover={{ y: -5 }}
                >
                  <div className="flex items-start gap-6">
                    <motion.div 
                      className={`w-14 h-14 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center flex-shrink-0`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <subService.icon className="w-7 h-7 text-white" />
                    </motion.div>
                    <div>
                      <h3 className="font-outfit text-xl font-semibold text-[#0A2540] mb-2 group-hover:text-[#2563EB] transition-colors">
                        {subService.title}
                      </h3>
                      <p className="text-slate-600 mb-4">{subService.description}</p>
                      <ul className="grid grid-cols-2 gap-2">
                        {subService.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-slate-500">
                            <CheckCircle2 className="w-4 h-4 text-[#2563EB] flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* SEO Details (only for digital marketing) */}
      {service.seoDetails && (
        <section className="section-padding">
          <div className="container-custom">
            <FadeInUp className="text-center mb-12">
              <h2 className="font-outfit text-3xl font-bold text-[#0A2540]">
                {service.seoDetails.title}
              </h2>
            </FadeInUp>
            
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" staggerDelay={0.08}>
              {service.seoDetails.services.map((seoService, index) => (
                <StaggerItem key={index}>
                  <motion.div 
                    className="bg-white p-6 rounded-xl border border-slate-100 h-full"
                    whileHover={{ y: -5, borderColor: '#2563EB' }}
                  >
                    <h3 className="font-semibold text-[#0A2540] mb-2">{seoService.name}</h3>
                    <p className="text-sm text-slate-600">{seoService.description}</p>
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      <section className={`section-padding ${service.seoDetails ? 'bg-slate-50' : ''}`}>
        <div className="container-custom">
          <FadeInUp className="text-center mb-12">
            <h2 className="font-outfit text-3xl font-bold text-[#0A2540]">
              Why Choose Us
            </h2>
          </FadeInUp>
          
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" staggerDelay={0.08}>
            {service.benefits.map((benefit, index) => (
              <StaggerItem key={index}>
                <motion.div 
                  className="flex items-start gap-4 p-4"
                  whileHover={{ x: 5 }}
                >
                  <div className={`w-10 h-10 ${service.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <CheckCircle2 className={`w-5 h-5 ${service.iconColor}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A2540] mb-1">{benefit.title}</h3>
                    <p className="text-sm text-slate-600">{benefit.description}</p>
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Process Section */}
      <section className={`section-padding bg-gradient-to-br ${service.color}`}>
        <div className="container-custom">
          <FadeInUp className="text-center mb-12">
            <h2 className="font-outfit text-3xl font-bold text-white">
              Our Process
            </h2>
          </FadeInUp>
          
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" staggerDelay={0.15}>
            {service.process.map((step, index) => (
              <StaggerItem key={index}>
                <motion.div 
                  className="bg-white/10 backdrop-blur-sm border border-white/20 p-6 rounded-2xl text-center relative"
                  whileHover={{ y: -5 }}
                >
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-lg font-bold text-[#0A2540]">{step.step}</span>
                  </div>
                  <h3 className="font-outfit text-lg font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-white/70 text-sm">{step.description}</p>
                  
                  {index < service.process.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 -translate-y-1/2">
                      <ChevronRight className="w-6 h-6 text-white/50" />
                    </div>
                  )}
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding">
        <div className="container-custom">
          <FadeInUp className="text-center mb-12">
            <h2 className="font-outfit text-3xl font-bold text-[#0A2540]">
              Frequently Asked Questions
            </h2>
          </FadeInUp>
          
          <FadeInUp delay={0.2} className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {service.faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white border border-slate-100 rounded-2xl px-6 data-[state=open]:shadow-lg transition-shadow"
                >
                  <AccordionTrigger className="text-left font-medium text-[#0A2540] hover:no-underline py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeInUp>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="section-padding bg-[#0A2540] relative overflow-hidden">
        <AnimatedBlobs />
        <div className="container-custom text-center relative z-10">
          <FadeInUp>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
          </FadeInUp>
          
          <FadeInUp delay={0.2}>
            <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
              Let's discuss how {service.title.toLowerCase()} can transform your business.
            </p>
          </FadeInUp>
          
          <FadeInUp delay={0.4}>
            <Link to="/contact">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-10 py-6 text-lg font-medium shadow-lg shadow-[#2563EB]/25">
                  {service.ctaText}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </Link>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
};

export default ServicesOverviewPage;
